// Auto guest messages — daily queue of welcome (D-1) and thank-you (D+1) texts, one-tap send.
import React, { useCallback, useEffect, useState } from 'react';
import { Copy, Loader2, MessageCircle, MessageSquare, RefreshCw, Send, SkipForward, Sparkles } from 'lucide-react';
import { apiCall } from './_shared';

const KIND = {
  welcome: { label: 'Welcome · arrives tomorrow', cls: 'bg-emerald-100 text-emerald-800' },
  thank_you: { label: 'Thank you · left yesterday', cls: 'bg-sky-100 text-sky-800' },
  pre_arrival: { label: 'Pre-arrival · ETA & extras (3 days before)', cls: 'bg-orange-100 text-orange-800' },
  pulse: { label: 'In-stay pulse 👍/👎 · day 2', cls: 'bg-violet-100 text-violet-800' },
};

const AutoMessagesCard = () => {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState('pending');
  const [busy, setBusy] = useState('');
  const [open, setOpen] = useState(null);

  const load = useCallback(async () => {
    try { setData(await apiCall(`/api/admin/reception/auto-messages?status=${status}`)); } catch { /* ignore */ }
  }, [status]);
  useEffect(() => { load(); }, [load]);

  const run = async () => {
    setBusy('run');
    try { const r = await apiCall('/api/admin/reception/auto-messages/run', { method: 'POST' }); await load(); if (!r.created) alert('Queue is up to date — nothing new to prepare.'); }
    catch (e) { alert(e.message); } finally { setBusy(''); }
  };
  const send = async (it, channel) => {
    setBusy(it.id);
    try {
      const r = await apiCall(`/api/admin/reception/auto-messages/${it.id}/sent`, { method: 'POST', body: JSON.stringify({ channel }) });
      if (channel === 'copy') await navigator.clipboard?.writeText(it.text);
      else if (r.link) window.open(r.link, '_blank', 'noopener');
      await load();
    } catch (e) { alert(e.message); } finally { setBusy(''); }
  };
  const skip = async (it) => {
    setBusy(it.id);
    try { await apiCall(`/api/admin/reception/auto-messages/${it.id}/skip`, { method: 'POST' }); await load(); }
    catch (e) { alert(e.message); } finally { setBusy(''); }
  };

  const counts = data?.counts || {};
  const chips = ['pending', 'sent', 'skipped'].map((s) => (
    <button key={s} onClick={() => setStatus(s)} className={`px-2.5 py-1 rounded-full text-xs font-semibold ${status === s ? 'bg-slate-900 text-white' : 'bg-white border text-slate-700 hover:bg-slate-50'}`} data-testid={`automsg-filter-${s}`}>
      {s} ({counts[s] ?? 0})
    </button>
  ));
  const items = (data?.items || []).map((it) => (
    <li key={it.id} className="py-2" data-testid={`automsg-item-${it.id}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`text-[11px] px-2 py-0.5 rounded-full font-semibold ${KIND[it.kind]?.cls}`}>{KIND[it.kind]?.label || it.kind}</span>
        <span className="text-sm font-semibold text-slate-900">{it.guest_name}</span>
        <span className="text-xs text-slate-500">{it.room_name} · {it.check_in} → {it.check_out} · {it.lang?.toUpperCase()}{it.phone ? ` · ${it.phone}` : ' · no phone'}</span>
        <button onClick={() => setOpen(open === it.id ? null : it.id)} className="text-xs text-orange-600 hover:underline" data-testid={`automsg-toggle-${it.id}`}>{open === it.id ? 'hide' : 'preview'}</button>
        {it.status === 'pending' ? (
          <span className="ml-auto flex gap-1">
            <button onClick={() => send(it, 'whatsapp')} disabled={busy === it.id || !it.phone} className="px-2 py-1 rounded bg-emerald-600 text-white text-xs font-semibold flex items-center gap-1 disabled:opacity-40" title={it.phone ? 'Open in WhatsApp' : 'No phone number on file'} data-testid={`automsg-wa-${it.id}`}><Send className="w-3 h-3" /> WhatsApp</button>
            <button onClick={() => send(it, 'sms')} disabled={busy === it.id || !it.phone} className="px-2 py-1 rounded bg-sky-600 text-white text-xs font-semibold flex items-center gap-1 disabled:opacity-40" data-testid={`automsg-sms-${it.id}`}><MessageSquare className="w-3 h-3" /> SMS</button>
            <button onClick={() => send(it, 'copy')} disabled={busy === it.id} className="px-2 py-1 rounded border text-xs font-semibold flex items-center gap-1" data-testid={`automsg-copy-${it.id}`}><Copy className="w-3 h-3" /> Copy</button>
            <button onClick={() => skip(it)} disabled={busy === it.id} className="px-2 py-1 rounded border text-xs text-slate-500 flex items-center gap-1" data-testid={`automsg-skip-${it.id}`}><SkipForward className="w-3 h-3" /> Skip</button>
          </span>
        ) : (
          <span className="ml-auto text-xs text-slate-500">{it.status} {it.sent_by ? `by ${it.sent_by} via ${it.channel}` : ''}</span>
        )}
      </div>
      {open === it.id && <p className="mt-1 text-xs text-slate-600 whitespace-pre-wrap bg-slate-50 rounded p-2" data-testid={`automsg-text-${it.id}`}>{it.text}</p>}
    </li>
  ));

  return (
    <div className="bg-white rounded-xl border p-4" data-testid="auto-messages-card">
      <div className="flex items-center gap-2 flex-wrap mb-2">
        <h3 className="font-bold text-slate-900 flex items-center gap-2"><Sparkles className="w-4 h-4 text-orange-500" /> Guest messages today</h3>
        <span className="text-xs text-slate-500 hidden sm:inline">Welcome texts for tomorrow's arrivals · thank-you for yesterday's departures (prepared 09:30 daily)</span>
        <div className="flex gap-1.5 ml-auto">{chips}</div>
        <button onClick={run} disabled={busy === 'run'} className="px-2.5 py-1 rounded-lg border text-xs font-semibold flex items-center gap-1 hover:bg-slate-50" data-testid="automsg-run">
          {busy === 'run' ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Prepare now
        </button>
      </div>
      <ul className="divide-y" data-testid="automsg-list">
        {items.length ? items : <li className="py-4 text-sm text-slate-400 flex items-center gap-2"><MessageCircle className="w-4 h-4" /> No {status} messages.</li>}
      </ul>
    </div>
  );
};

export default AutoMessagesCard;
